import { Button, ModalBody, ModalHeader } from "reactstrap";
import { Trans, useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { keyBy, reduce } from "lodash";
import { useConfirmModal, useXeroClient } from "../../../hooks";
import { NotificationManager } from "react-notifications";
import { humanError } from "services/util";

const useSelected = (items, pk, selected) => {
  const itemsByPk = useMemo(() => keyBy(items, pk), [items, pk]);

  return useMemo(
    () =>
      reduce(
        selected,
        (acc, v, k) => {
          if (v && itemsByPk[k]) acc.push(itemsByPk[k]);
          return acc;
        },
        []
      ),
    [itemsByPk, selected]
  );
};

export const BulkDraftActions = ({
  selected: selectedProp,
  url,
  pk,
  items,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const selected = useSelected(items, pk, selectedProp);
  const [action, setAction] = useState();
  const num = selected.length;
  const { Modal, isOpen, setIsOpen } = useConfirmModal();
  const client = useXeroClient();

  const actionText = useMemo(
    () =>
      ({
        SUBMITTED: t("submit for approval"),
        DELETED: t("delete"),
        AUTHORISED: t("approve"),
      }[action]),
    [t, action]
  );

  const openModel = (action) => () => {
    setAction(action);
    setIsOpen(true);
  };

  async function onConfirm() {
    const failed = [];
    const errors = [];
    const success = [];

    for (const i of selected) {
      // some fields needs to be stripped from the payload
      const payload = Object.fromEntries(
        Object.entries(i).filter(
          ([key]) =>
            !key.includes("String") &&
            !["ID", "BrandingThemeID", "HasErrors", "HasAttachments", "IsDiscounted"].includes(key)
        )
      );
      
      try {
        await client.put(`${url}`, {
          ...payload,
          Status: action,
        });
      } catch (err) {
        console.error(err);
        failed.push(i);
        errors.push(humanError(err, 'Please fill all required fields' + actionText, t));
        continue;
      }
      success.push(i);
    }
    
    if (failed.length) {
      for (const [index, error] of errors.entries()) {
        const document = selected?.[index];
        NotificationManager.error(t('Error at document {{ doc_number }}: {{ error }}', { doc_number: document.InvoiceNumber || document.QuoteNumber || document.CreditNoteNumber , error }));
      }
    }

    if (success.length) {
      NotificationManager.success(
        t("{{ success }} succeeded to {{ action }}", {
          success: success.length,
          action: actionText,
        })
      );
    }

    await onSuccess?.();
  }

  return (
    <div className="">
      {isOpen && (
        <Modal onConfirm={onConfirm}>
          <ModalHeader>
            <Trans>Confirm</Trans>
          </ModalHeader>
          <ModalBody>
            <Trans>
              You have selected {{ num }} item to {{ action: actionText }}.
            </Trans>
          </ModalBody>
        </Modal>
      )}

      <Button
        onClick={openModel("SUBMITTED")}
        size="sm"
        color="secondary"
        disabled={!num}
        className="ml-0 mr-2"
      >
        <Trans>Submit for approval</Trans>
      </Button>
      <Button
        onClick={openModel("AUTHORISED")}
        size="sm"
        color="secondary"
        disabled={!num}
        className="ml-0 mr-2"
      >
        <Trans>Approve</Trans>
      </Button>
      <Button
        onClick={openModel("DELETED")}
        size="sm"
        color="danger"
        disabled={!num}
        className="ml-0 mr-2"
      >
        <span >
          <Trans>Delete</Trans>
        </span>
      </Button>
    </div>
  );
};
